import React, { useEffect, useState } from 'react';
import { analyticsAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Loader2, Eye, Users, TrendingUp, ExternalLink } from 'lucide-react';

function FunnelChart({ steps }) {
  const top = Math.max(1, steps[0]?.count || 0);
  return (
    <div className="space-y-2" data-testid="funnel-chart">
      {steps.map((s, i) => {
        const pct = Math.round((s.count / top) * 100);
        const dropOff = i > 0 && steps[i - 1].count > 0
          ? Math.round((s.count / steps[i - 1].count) * 100)
          : null;
        return (
          <div key={s.key} className="relative" data-testid={`funnel-step-${s.key}`}>
            <div className="flex items-center gap-3">
              <div className="w-8 text-lg text-center">{s.icon}</div>
              <div className="flex-1">
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-sm font-semibold text-slate-800">{s.label}</span>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                      {s.count.toLocaleString('fr-FR')}
                    </span>
                    {dropOff !== null && (
                      <span className={`text-xs font-bold ${dropOff >= 30 ? 'text-green-700' : dropOff >= 10 ? 'text-orange-600' : 'text-red-600'}`}>
                        ({dropOff}%)
                      </span>
                    )}
                  </div>
                </div>
                <div className="h-6 bg-slate-100 rounded overflow-hidden">
                  <div
                    className={`h-full transition-all ${i === steps.length - 1 ? 'bg-emerald-500' : 'bg-amber-400'}`}
                    style={{ width: `${Math.max(2, pct)}%` }}
                  />
                </div>
              </div>
            </div>
          </div>
        );
      })}
      <p className="text-[11px] text-slate-500 italic mt-3">
        Les pourcentages en couleur indiquent le taux de passage d&apos;une étape à la suivante — 🟢 ≥ 30 % excellent, 🟠 10-29 % moyen, 🔴 &lt; 10 % à optimiser.
      </p>
    </div>
  );
}

export default function AnalyticsWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const { data } = await analyticsAPI.summary();
        if (!cancelled) setData(data);
      } catch (e) {
        console.error(e);
      }
      if (!cancelled) setLoading(false);
    };
    load();
    const timer = setInterval(load, 60_000);  // refresh every 60s
    return () => { cancelled = true; clearInterval(timer); };
  }, []);

  if (loading) {
    return (
      <Card><CardContent className="p-8 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></CardContent></Card>
    );
  }
  if (!data) return null;

  const { stats, top_pages, top_referrers, daily, funnel } = data;

  const maxDaily = Math.max(1, ...(daily || []).map(d => d.views));
  const prettyPath = (p) => p === '/' ? '🏠 Accueil' : p.length > 40 ? p.slice(0, 37) + '…' : p;
  const prettyRef = (r) => {
    try { return new URL(r).hostname; } catch { return r.length > 40 ? r.slice(0, 37) + '…' : r; }
  };

  return (
    <div className="space-y-4" data-testid="analytics-widget">
      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {['today', 'week', 'month'].map(k => (
          <Card key={k} className="border-slate-200" data-testid={`analytics-card-${k}`}>
            <CardContent className="p-5">
              <div className="text-xs uppercase font-bold text-slate-500 mb-1">{stats[k].label}</div>
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-3xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  {stats[k].views.toLocaleString('fr-FR')}
                </span>
                <span className="text-xs text-slate-500 flex items-center gap-1"><Eye className="w-3 h-3" /> vues</span>
              </div>
              <div className="text-sm text-slate-700 flex items-center gap-1">
                <Users className="w-4 h-4 text-slate-500" />
                <strong>{stats[k].visitors.toLocaleString('fr-FR')}</strong> visiteurs uniques
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Daily bar chart */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-sm font-bold uppercase text-slate-500 flex items-center gap-2">
            <TrendingUp className="w-4 h-4" /> Trafic 14 derniers jours
          </CardTitle>
        </CardHeader>
        <CardContent>
          {(!daily || daily.length === 0) ? (
            <div className="text-center text-slate-400 py-8 text-sm">Aucune visite enregistrée pour l&apos;instant. Partagez votre pub Facebook !</div>
          ) : (
            <div className="flex items-end gap-1 h-32">
              {daily.map(d => (
                <div key={d.day} className="flex-1 flex flex-col items-center gap-1 group relative" data-testid={`bar-${d.day}`}>
                  <div
                    className="w-full bg-amber-400 hover:bg-amber-500 rounded-t transition"
                    style={{ height: `${Math.max(4, (d.views / maxDaily) * 100)}%` }}
                    title={`${d.day}: ${d.views} vues, ${d.uniques} visiteurs`}
                  />
                  <div className="text-[9px] text-slate-500 whitespace-nowrap">{d.day.slice(5)}</div>
                  <div className="absolute -top-6 hidden group-hover:block bg-slate-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap z-10">
                    {d.views} vues · {d.uniques} visiteurs
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Conversion funnel */}
      {funnel && funnel.length > 0 && (
        <Card className="border-slate-200" data-testid="funnel-card">
          <CardHeader>
            <CardTitle className="text-sm font-bold uppercase text-slate-500">🎯 Entonnoir de conversion (30 derniers jours)</CardTitle>
          </CardHeader>
          <CardContent>
            <FunnelChart steps={funnel} />
          </CardContent>
        </Card>
      )}

      {/* Top pages + Top referrers */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-sm font-bold uppercase text-slate-500">📄 Pages les plus visitées (30j)</CardTitle></CardHeader>
          <CardContent>
            {(!top_pages || top_pages.length === 0) ? (
              <p className="text-sm text-slate-400 py-4 text-center">Aucune donnée.</p>
            ) : (
              <div className="space-y-2">
                {top_pages.map(p => (
                  <div key={p.path} className="flex items-center justify-between text-sm border-b last:border-b-0 py-1.5" data-testid={`top-page-${p.path}`}>
                    <span className="font-mono text-xs text-slate-700 truncate flex-1 mr-2">{prettyPath(p.path)}</span>
                    <span className="text-slate-900 font-bold">{p.views.toLocaleString('fr-FR')}</span>
                    <span className="text-xs text-slate-500 ml-2">({p.uniques} uniq.)</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader><CardTitle className="text-sm font-bold uppercase text-slate-500 flex items-center gap-1">🔗 Sources de trafic (30j)</CardTitle></CardHeader>
          <CardContent>
            {(!top_referrers || top_referrers.length === 0) ? (
              <p className="text-sm text-slate-400 py-4 text-center">Trafic direct uniquement. Publiez sur Facebook/Insta pour voir les sources !</p>
            ) : (
              <div className="space-y-2">
                {top_referrers.map(r => (
                  <div key={r.referrer} className="flex items-center justify-between text-sm border-b last:border-b-0 py-1.5">
                    <a href={r.referrer} target="_blank" rel="noreferrer" className="text-blue-700 hover:underline text-xs truncate flex-1 mr-2 flex items-center gap-1">
                      <ExternalLink className="w-3 h-3 flex-shrink-0" /> {prettyRef(r.referrer)}
                    </a>
                    <span className="text-slate-900 font-bold">{r.views.toLocaleString('fr-FR')}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
